import adsApi from "../../../services/adsApi";
import apiConfig from "../../../config/apiConfig";

let memoryCache = null;
let memoryCacheAt = 0;

const CACHE_TTL_MS = 5 * 60 * 1000;
const STORAGE_KEY = "mi_ads_runtime_catalog_v2";
const STORAGE_KEY_AT = "mi_ads_runtime_catalog_at_v2";

const now = () => Date.now();

function readLocalStorageCache() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const rawAt = localStorage.getItem(STORAGE_KEY_AT);
    if (!raw || !rawAt) {
      return null;
    }
    const parsed = JSON.parse(raw);
    const parsedAt = Number(rawAt);
    if (!parsed || !Number.isFinite(parsedAt)) {
      return null;
    }
    if (now() - parsedAt > CACHE_TTL_MS) {
      return null;
    }
    return { data: parsed, at: parsedAt };
  } catch (error) {
    return null;
  }
}

function saveLocalStorageCache(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    localStorage.setItem(STORAGE_KEY_AT, String(now()));
  } catch (error) {
    // best-effort cache
  }
}

function normalizeCatalogResponse(payload) {
  if (!payload) {
    return {};
  }

  if (payload.ads && typeof payload.ads === "object") {
    return payload.ads;
  }

  if (payload.data && payload.data.ads && typeof payload.data.ads === "object") {
    return payload.data.ads;
  }

  if (Array.isArray(payload)) {
    return payload.reduce((acc, item) => {
      if (item && item.slotKey) {
        acc[item.slotKey] = item;
      }
      return acc;
    }, {});
  }

  return {};
}

function getAdsApiBaseUrl() {
  const base = apiConfig.endpoints.adsService.baseURL || "";

  return base ? base.replace(/\/+$/, "") : "";
}

function toAbsoluteMediaUrl(url) {
  if (!url || typeof url !== "string") return url;
  if (/^https?:\/\//i.test(url)) return url;
  if (!url.startsWith("/")) return url;

  const base = getAdsApiBaseUrl();
  if (!base) return url;
  return `${base}${url}`;
}

function normalizeCatalogMedia(catalog) {
  if (!catalog || typeof catalog !== "object") return {};

  return Object.entries(catalog).reduce((acc, [slotKey, item]) => {
    const metadata = item?.metadata && typeof item.metadata === "object" ? item.metadata : {};

    acc[slotKey] = {
      ...item,
      imageSrc: toAbsoluteMediaUrl(item?.imageSrc),
      metadata: {
        ...metadata,
        imagePreview: toAbsoluteMediaUrl(metadata.imagePreview),
      },
    };

    return acc;
  }, {});
}

export async function getRuntimeAdCatalog() {
  if (!apiConfig.endpoints.adsService.baseURL) {
    return {};
  }

  if (memoryCache && now() - memoryCacheAt <= CACHE_TTL_MS) {
    return memoryCache;
  }

  if (typeof window !== "undefined") {
    const fromStorage = readLocalStorageCache();
    if (fromStorage) {
      memoryCache = fromStorage.data;
      memoryCacheAt = fromStorage.at;
      return memoryCache;
    }
  }

  try {
    const payload = await adsApi.getCatalog();
    const normalized = normalizeCatalogMedia(normalizeCatalogResponse(payload));
    memoryCache = normalized;
    memoryCacheAt = now();
    if (typeof window !== "undefined") {
      saveLocalStorageCache(normalized);
    }
    return normalized;
  } catch (error) {
    return {};
  }
}
